import { Box, Divider, ListSubheader, Typography } from '@mui/material';
import { CopyPlus, Download, Info } from 'lucide-react';
import { useCallback, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ModalityChip from '~/components/Chip/ModalityChip';
import DatasetBox from '~/components/DatasetBox';
import DialogFlyout from '~/components/Menu/DialogFlyout';
import MenuAction from '~/components/Menu/MenuAction';
import MenuChoice from '~/components/Menu/MenuChoice';
import MenuFlyout from '~/components/Menu/MenuFlyout';
import MenuFlyoutRow from '~/components/Menu/MenuFlyoutRow';
import { useObjectDetailsDialog } from '~/components/Properties/ObjectDetailsDialog';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { toSeriesInstanceUid } from '~/domain/dicom/uids';
import { useDicomExport } from '~/hooks/export/useDicomExport';
import { useAvailableStructureSetsView } from '~/hooks/domain/useAvailableStructureSetsView';
import usePrimaryImage, { AvailableImage } from '~/hooks/image/usePrimaryImage';
import { useImageDownload } from '~/hooks/image/useImageDownload';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { gridRowImagesState } from '~/state/atoms/viewportGridAtoms';
import { formatDate } from '~/utils/date';
import {
    acquisitionDateOf,
    seriesNameOf,
    seriesNumberOf,
} from '~/utils/transforms/imageDataTransforms';
import ActiveDatasetDialogs from './ActiveDatasetDialogs';
import DatasetButton from './DatasetButton';
import ImageListMeta from './ImageListMeta';
import OpenDatasetsPanel from './OpenDatasetsPanel';
import useOpenImageRows from './useOpenImageRows';
import { PickerMenu } from './PickerMenu';
import StructureSetLine from './StructureSetLine';
import StructureSetOption from './StructureSetOption';
import useStructureSetActions from './useStructureSetActions';

/** Width of the Change Image and Change Structure Set flyouts. */
const FLYOUT_WIDTH_PX = 250;
/** Width of the Open Datasets flyout. */
const OPEN_DATASETS_WIDTH_PX = 320;

type Flyout = 'open' | 'image' | 'structureSet';

/**
 * The date an available image was acquired: for a loaded image, its first
 * slice's acquisition date, else its series date ({@link acquisitionDateOf});
 * for one known only from the session, the session's date. PHI: mask
 * wherever rendered.
 */
function acquiredOn(image: AvailableImage): Date | undefined {
    return image.imageObject
        ? formatDate(acquisitionDateOf(image.imageObject) ?? '')
        : image.sessionImageInfo?.date;
}

/**
 * The datasets the user works on, in one sunken box: one button that names
 * the primary image and, under it, the structure set drawn on it. The button
 * opens one menu, as wide as the box. Its first row opens Open Datasets,
 * which shows images of the patient in a grid row each, downloading first
 * one known only from the session. The next two rows name the image and the
 * set, and each opens a flyout beside the menu. The flyout starts with the
 * dataset's actions, Properties, which opens its details dialog, and
 * Download, then lists the datasets that can take its place. The set row is
 * left out while the image has no set to show or to load. Then come the
 * structure set actions, under their heading. Rename opens the set's
 * details dialog with the label editor.
 *
 * Choosing a loaded image makes it the primary image and then offers the
 * image's structure sets: no set is chosen for the user. Choosing an image
 * known only from the session downloads it; the import makes it the primary
 * image and the set list stays closed. A series that must be reformatted
 * before it can be shown is not offered. Choosing a set of another image
 * asks before the image changes; for a set not downloaded yet, once the
 * download has told which image it belongs to.
 */
export default function DatasetPicker() {
    const intl = useIntl();
    const unknownSeries = intl.formatMessage({ id: 'unknown.series' });
    const [boxEl, setBoxEl] = useState<HTMLElement | null>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [flyout, setFlyout] = useState<Flyout | null>(null);
    const [openRowEl, setOpenRowEl] = useState<HTMLLIElement | null>(null);
    const [imageRowEl, setImageRowEl] = useState<HTMLLIElement | null>(null);
    const [setRowEl, setSetRowEl] = useState<HTMLLIElement | null>(null);
    const [isRenaming, setIsRenaming] = useState(false);
    const imageDialog = useObjectDetailsDialog();
    const structureSetDialog = useObjectDetailsDialog();

    const { availableImages, primaryImage, handleChangePrimaryImage } =
        usePrimaryImage(true);
    const structureSet = useRecoilValue(currentStructureSetState);
    const availableStructureSets = useAvailableStructureSetsView(true);
    const { pickStructureSet } = useConfirmedSelection();
    const rowImages = useRecoilValue(gridRowImagesState);
    const openImageRows = useOpenImageRows(availableImages);

    const images = availableImages.filter((image) => !image.needsReformat);
    const currentImage = images.find(
        (image) => image.seriesInstanceUID === primaryImage?.seriesUid
    );
    // A loaded image opens as it is; one known only from the session is
    // downloaded on Open.
    const openableImages = images.map((image) => ({
        seriesInstanceUid: toSeriesInstanceUid(image.seriesInstanceUID),
        modality: image.modality,
        name: seriesNameOf(image.label, unknownSeries),
        date: acquiredOn(image),
        seriesNumber: seriesNumberOf(image.imageObject),
    }));
    // A set not downloaded yet names no image until it is, so it is offered
    // on every image; once downloaded, one of another image asks before the
    // image changes. A loaded set is offered only on its own image, so a
    // set of no image is never offered, with or without a primary image.
    const setsOfImage = availableStructureSets.filter(
        (set) =>
            !set.isLoaded ||
            (primaryImage !== undefined &&
                set.imageSeriesUid === primaryImage.seriesUid)
    );
    // With no set to show or to load, the set row offers nothing: Create
    // New and Generate New below it are the only ways on.
    const hasStructureSetChoice =
        structureSet !== undefined || setsOfImage.length > 0;

    // The flyout closes first, handing focus back to its row; the menu then
    // hands it back to the dataset button. Closed in one render, the flyout
    // would hand it to a row the menu is taking away, and a dialog opened
    // from the flyout would give focus back to nothing when it closes.
    const closeMenu = useCallback(() => {
        flushSync(() => setFlyout(null));
        setIsMenuOpen(false);
    }, []);

    const toggleFlyout = (which: Flyout) =>
        setFlyout((open) => (open === which ? null : which));

    const imageDownload = useImageDownload(primaryImage);
    const { handleDownloadStructureSet } = useDicomExport();

    const { items: structureSetItems, dialogs } = useStructureSetActions({
        close: closeMenu,
        onRename: () => {
            setIsRenaming(true);
            structureSetDialog.open();
        },
    });

    const imageName = (image: AvailableImage) => (
        <SensitiveInfoTypography>
            {seriesNameOf(image.label, unknownSeries)}
        </SensitiveInfoTypography>
    );

    // The image's sets are offered only once it is the primary image: a
    // refused change leaves the list closed, never showing the old image's
    // sets under the new one's name.
    const chooseImage = async (image: AvailableImage) => {
        setFlyout(null);
        if (image === currentImage) {
            return;
        }
        if (await handleChangePrimaryImage(image)) {
            setFlyout('structureSet');
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <DatasetBox ref={setBoxEl}>
                <DatasetButton
                    open={isMenuOpen}
                    onClick={() => setIsMenuOpen(true)}
                    data-testid="structureset-menu-button"
                    first={
                        currentImage ? (
                            <>
                                <ModalityChip
                                    modality={currentImage.modality}
                                    size="small"
                                />
                                <Typography
                                    component="span"
                                    fontSize={13}
                                    fontWeight={500}
                                    lineHeight="20px"
                                    noWrap
                                    flex={1}
                                    minWidth={0}
                                    data-testid="primary-image-name"
                                >
                                    {imageName(currentImage)}
                                </Typography>
                                <ImageListMeta
                                    date={acquiredOn(currentImage)}
                                    seriesNumber={seriesNumberOf(
                                        currentImage.imageObject
                                    )}
                                />
                            </>
                        ) : (
                            <Typography
                                component="span"
                                fontSize={13}
                                lineHeight="20px"
                                color="text.secondary"
                                noWrap
                            >
                                <FormattedMessage id="datasetPicker.noImage" />
                            </Typography>
                        )
                    }
                    second={
                        <StructureSetLine
                            hasSetToLoad={setsOfImage.length > 0}
                        />
                    }
                />
            </DatasetBox>
            <PickerMenu
                open={isMenuOpen}
                boxEl={boxEl}
                onClose={closeMenu}
                data-testid="dataset-menu"
            >
                <MenuFlyoutRow
                    ref={setOpenRowEl}
                    open={flyout === 'open'}
                    onToggle={() => toggleFlyout('open')}
                    onOpen={() => setFlyout('open')}
                    Icon={CopyPlus}
                    name={<FormattedMessage id="datasetPicker.openDatasets" />}
                    popup="dialog"
                    data-testid="open-datasets-button"
                />
                <MenuFlyoutRow
                    ref={setImageRowEl}
                    open={flyout === 'image'}
                    onToggle={() => toggleFlyout('image')}
                    onOpen={() => setFlyout('image')}
                    leading={
                        currentImage && (
                            <ModalityChip
                                modality={currentImage.modality}
                                size="small"
                            />
                        )
                    }
                    name={
                        currentImage ? (
                            imageName(currentImage)
                        ) : (
                            <FormattedMessage id="datasetPicker.noImage" />
                        )
                    }
                    data-testid="change-image-button"
                />
                {hasStructureSetChoice && (
                    <MenuFlyoutRow
                        ref={setSetRowEl}
                        open={flyout === 'structureSet'}
                        onToggle={() => toggleFlyout('structureSet')}
                        onOpen={() => setFlyout('structureSet')}
                        leading={<ModalityChip modality="RTSS" size="small" />}
                        name={
                            structureSet ? (
                                <SensitiveInfoTypography>
                                    {structureSet.structureSetLabel}
                                </SensitiveInfoTypography>
                            ) : (
                                <FormattedMessage id="datasetPicker.loadStructureSet" />
                            )
                        }
                        offer={!structureSet}
                        data-testid="change-structureset-button"
                    />
                )}
                <Divider />
                <ListSubheader>
                    <FormattedMessage id="structureSet" />
                </ListSubheader>
                {structureSetItems}
            </PickerMenu>
            <DialogFlyout
                anchorEl={openRowEl}
                open={isMenuOpen && flyout === 'open'}
                onClose={() => setFlyout(null)}
                width={OPEN_DATASETS_WIDTH_PX}
                aria-label={intl.formatMessage({
                    id: 'datasetPicker.openDatasets',
                })}
                data-testid="open-datasets-panel"
            >
                <OpenDatasetsPanel
                    images={openableImages}
                    shown={rowImages}
                    primarySeriesUid={currentImage?.seriesInstanceUID}
                    onOpen={(seriesInstanceUids) => {
                        closeMenu();
                        void openImageRows(seriesInstanceUids);
                    }}
                    onCancel={() => setFlyout(null)}
                />
            </DialogFlyout>
            <MenuFlyout
                anchorEl={imageRowEl}
                open={isMenuOpen && flyout === 'image'}
                onClose={() => setFlyout(null)}
                width={FLYOUT_WIDTH_PX}
                data-testid="primary-image-menu"
            >
                {primaryImage && (
                    <MenuAction
                        icon={<Info />}
                        label={
                            <FormattedMessage id="datasetPicker.imageProperties" />
                        }
                        onClick={() => {
                            closeMenu();
                            imageDialog.open();
                        }}
                        data-testid="image-properties-menu-item"
                    />
                )}
                {imageDownload && (
                    <MenuAction
                        icon={<Download />}
                        label={
                            <FormattedMessage id="datasetPicker.downloadImage" />
                        }
                        // Left open, the row shows the download's progress.
                        onClick={imageDownload.download}
                        busy={imageDownload.busy}
                        disabledReason={imageDownload.refusal}
                        data-testid="download-primary-image-button"
                    />
                )}
                {primaryImage && <Divider />}
                <ListSubheader>
                    <FormattedMessage id="datasetPicker.changeImage" />
                </ListSubheader>
                {images.map((image) => {
                    return (
                        <MenuChoice
                            key={image.seriesInstanceUID}
                            selected={image === currentImage}
                            onClick={() => void chooseImage(image)}
                            chip={
                                <ModalityChip
                                    modality={image.modality}
                                    size="small"
                                />
                            }
                            name={imageName(image)}
                            meta={
                                <ImageListMeta
                                    date={acquiredOn(image)}
                                    seriesNumber={seriesNumberOf(
                                        image.imageObject
                                    )}
                                />
                            }
                            data-testid="primary-image-option"
                        />
                    );
                })}
            </MenuFlyout>
            <MenuFlyout
                anchorEl={setRowEl}
                open={isMenuOpen && flyout === 'structureSet'}
                onClose={() => setFlyout(null)}
                width={FLYOUT_WIDTH_PX}
                data-testid="structure-set-menu"
            >
                {structureSet && (
                    <MenuAction
                        icon={<Info />}
                        label={
                            <FormattedMessage id="datasetPicker.structureSetProperties" />
                        }
                        onClick={() => {
                            closeMenu();
                            structureSetDialog.open();
                        }}
                        data-testid="structure-set-properties-menu-item"
                    />
                )}
                {structureSet && (
                    <MenuAction
                        icon={<Download />}
                        label={<FormattedMessage id="downloadStructures" />}
                        onClick={() => {
                            closeMenu();
                            // The export reports its own failure to the user.
                            void handleDownloadStructureSet(
                                structureSet.dicomUid
                            );
                        }}
                        data-testid="download-structureset-button"
                    />
                )}
                {structureSet && <Divider />}
                <ListSubheader>
                    <FormattedMessage
                        id={
                            structureSet
                                ? 'changeStructureSet'
                                : 'datasetPicker.loadStructureSet'
                        }
                    />
                </ListSubheader>
                {setsOfImage.map((set) => {
                    const isCurrent =
                        set.sopInstanceUid === structureSet?.dicomUid;
                    return (
                        <StructureSetOption
                            key={set.sopInstanceUid}
                            structureSet={set}
                            selected={isCurrent}
                            onClick={() => {
                                closeMenu();
                                if (!isCurrent) {
                                    pickStructureSet(set);
                                }
                            }}
                        />
                    );
                })}
            </MenuFlyout>
            <ActiveDatasetDialogs
                imageDialog={imageDialog}
                structureSetDialog={structureSetDialog}
                renaming={isRenaming}
                onRenamingChange={setIsRenaming}
            />
            {dialogs}
        </Box>
    );
}
